import type { FileView, UsageStatus } from '../types/documents.types'

export type UsageStatusInput = {
  required: number
  files: ReadonlyArray<Pick<FileView, 'status'>>
}

export type UsageStatusResult = {
  tally: number
  status: UsageStatus
}

/**
 * FR-011 badge precedence:
 * 1. MANQUANT   — zero files uploaded at all.
 * 2. INCOMPLET  — at least one file uploaded (even rejected) and tally < required.
 * 3. A_VERIFIER — tally >= required and at least one non-rejected file is pending review.
 * 4. COMPLET    — tally >= required and every non-rejected file is accepted.
 *
 * The `tally` counts only non-rejected files (ACCEPTED + pending). Rejected files do not
 * contribute to the required tally but their presence keeps the usage out of MANQUANT.
 *
 * Research §8: a usage with required === 0 and zero files is treated as COMPLET.
 */
export const computeUsageStatus = (
  input: UsageStatusInput
): UsageStatusResult => {
  const { required, files } = input
  const nonRejected = files.filter((f) => f.status !== 'REJECTED')
  const tally = nonRejected.length

  if (files.length === 0) {
    return { tally: 0, status: required === 0 ? 'COMPLET' : 'MANQUANT' }
  }

  if (tally < required) {
    return { tally, status: 'INCOMPLET' }
  }

  const hasPending = nonRejected.some((f) => f.status === null)
  if (hasPending) {
    return { tally, status: 'A_VERIFIER' }
  }

  return { tally, status: 'COMPLET' }
}
